import { i18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import { storeEp } from "@smp/utils";
import { setDefaultOptions } from "date-fns";
import {
  en as pluralsEn,
  es as pluralsEs,
  kk as pluralsKk,
  pt as pluralsPt,
  ro as pluralsRo,
  ru as pluralsRu,
  uk as pluralsUk,
} from "make-plural/plurals";
import { PropsWithChildren, useCallback, useEffect, useMemo } from "react";
import { useLoaderData } from "react-router-dom";
import { useMount } from "react-use";

import { ContextLanguage } from "@/context/language/language.context";
import { ELocale, IContextLanguage } from "@/context/language/language.types";
import { messagesLoader } from "@/loaders/messages_loader";
import { getDateFnsLocaleByString } from "@/utils";

const init = (locale: ELocale, messages: Record<string, string>) => {
  i18n.loadLocaleData("en", { plurals: pluralsEn });
  i18n.loadLocaleData("ru", { plurals: pluralsRu });
  i18n.loadLocaleData("es", { plurals: pluralsEs });
  i18n.loadLocaleData("pt", { plurals: pluralsPt });
  i18n.loadLocaleData("kk", { plurals: pluralsKk });
  i18n.loadLocaleData("ro", { plurals: pluralsRo });
  i18n.loadLocaleData("uk", { plurals: pluralsUk });

  i18n.load(locale, messages);
  i18n.activate(locale);

  setDefaultOptions({ locale: getDateFnsLocaleByString(i18n.locale) });
};

export const ProviderLanguage = ({ children }: PropsWithChildren<{}>) => {
  const { messages, locale } =
    useLoaderData<Awaited<ReturnType<typeof messagesLoader>>>();

  const saveLanguageCode = useCallback((locale: ELocale) => {
    if (typeof window !== "undefined") {
      window.epLanguageCode = locale;
    }

    storeEp.set("language_code", locale);
  }, []);

  const changeLanguage = useCallback<IContextLanguage["changeLanguage"]>(
    async (newLocale) => {
      const { messages } = await messagesLoader(newLocale);

      i18n.load(newLocale, messages);
      i18n.activate(newLocale);

      setDefaultOptions({ locale: getDateFnsLocaleByString(i18n.locale) });

      saveLanguageCode(newLocale);
    },
    [saveLanguageCode],
  );

  if (import.meta.env.SSR) {
    init(locale, messages);
  }

  useEffect(() => {}, [locale]);

  useMount(() => {
    saveLanguageCode(locale);
    init(locale, messages);
  });

  return useMemo(
    () => (
      <ContextLanguage.Provider
        value={{
          changeLanguage,
        }}
      >
        <I18nProvider i18n={i18n}>{children}</I18nProvider>
      </ContextLanguage.Provider>
    ),
    [changeLanguage, children],
  );
};
